import { Breakpoint } from "@material-ui/core";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Modal,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import {
  cloneElement,
  FC,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";

interface IModalWrapper {
  trigger: React.ReactNode;
  child: any;
  maxWidth: Breakpoint;
  title: string;
  onClose?: () => void;
  onOpen?: () => void;
  closeConfirmation?: boolean;
  confirmCloseMessage?: string;
}

const ModalWrapper: FC<IModalWrapper> = ({
  trigger,
  child,
  title,
  maxWidth,
  onClose,
  onOpen,
  closeConfirmation: confirmOnClose,
  confirmCloseMessage,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenModal = () => {
    setOpenModal(true);
    if (onOpen) onOpen();
  };

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    if (onClose) onClose();
    setShowCloseConfirm(false);
  }, [onClose]);

  const onCloseIconClick = (event: React.MouseEvent<HTMLElement>) => {
    if (confirmOnClose) {
      setAnchorEl(event.currentTarget);
      setShowCloseConfirm(true);
    } else {
      handleCloseModal();
    }
  };

  const ChildComponentWithHandleClose = useMemo(() => {
    return cloneElement(child as React.ReactElement, {
      callback: (data: any) => {
        handleCloseModal();
        child?.props?.callback?.(data);
      },
    });
  }, [child, handleCloseModal]); // Only re-create when `child` changes



  const TriggerComponentWithOpenModal = () => {
    const originalOnClick = (trigger as ReactElement).props.onClick;
  
    return cloneElement(trigger as ReactElement, {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        if (typeof originalOnClick === "function") {
          originalOnClick(event);
        }
        handleOpenModal();
      },
    });
  };

  return (
    <>
      <TriggerComponentWithOpenModal />

      <Modal
        id="modal-wrapper"
        open={openModal}
        onClose={confirmOnClose ? onCloseIconClick : handleCloseModal}
      >
        <Box
          sx={{
            minHeight: "100%",
            p: 3,
          }}
        >
          <Container
            maxWidth={maxWidth as unknown as Breakpoint}
            onClick={(e) => e.stopPropagation()}
          >
            <Paper elevation={12} sx={{ p: 3, px: 5, position: "relative" }}>
            <IconButton onClick={onCloseIconClick}>
                <Close />
              </IconButton>
              <Box display="flex" justifyContent="flex-end">
             

                <Popper
                  open={showCloseConfirm}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  sx={{ zIndex: 1500 }}
                  
                >
                  <Card elevation={8} sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography>
                      {confirmCloseMessage || "Are you sure you want to close?"}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                        size="small"
                        variant="text"
                        sx={{p:0}}
                        onClick={() => setShowCloseConfirm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleCloseModal}
                      >
                        Yes
                      </Button>
                     
                    </Box>
                  </Card>
                </Popper>
              </Box>

              <Box>
                <Box mb={2}>
                  <Typography variant="h5">{title}</Typography>
                </Box>

                <Box sx={{ maxHeight: "80vh", overflow: "auto" }}>
                  {ChildComponentWithHandleClose}
                </Box>
              </Box>
            </Paper>
          </Container>
        </Box>
      </Modal>
    </>
  );
};

export default ModalWrapper;
